import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../contexts/AuthContext'
import { useTheme } from '../contexts/ThemeContext'
import { Button, Input } from '../components/ui'
import { RiSunLine, RiMoonLine, RiCheckLine } from '@remixicon/react'
import Logo from '../components/ui/Logo'

export default function Register() {
  const { t } = useTranslation()
  const { register } = useAuth()
  const { resolvedTheme, setTheme } = useTheme()

  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    try {
      await register({ name, email, password })
      setSuccess(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.generic'))
    } finally {
      setIsLoading(false)
    }
  }

  const toggleTheme = () => {
    setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')
  }

  if (success) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-gray-950">
        {/* Theme toggle in top right */}
        <div className="absolute top-4 right-4">
          <button
            onClick={toggleTheme}
            className="rounded-md p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-50"
            aria-label="Toggle theme"
          >
            {resolvedTheme === 'dark' ? (
              <RiSunLine className="size-5" />
            ) : (
              <RiMoonLine className="size-5" />
            )}
          </button>
        </div>

        <div className="flex flex-1 items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
          <div className="w-full max-w-sm space-y-8 text-center">
            {/* Logo */}
            <Logo />

            {/* Success message */}
            <div className="rounded-md bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 p-6">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-900">
                <RiCheckLine className="size-6 text-emerald-600 dark:text-emerald-400" />
              </div>
              <h2 className="text-lg font-semibold text-emerald-900 dark:text-emerald-100 mb-2">
                {t('auth.registrationSuccess')}
              </h2>
              <p className="text-sm text-emerald-700 dark:text-emerald-300">
                {t('auth.pendingApproval')}
              </p>
            </div>

            <Link
              to="/login"
              className="inline-flex font-medium text-primary-500 hover:text-primary-600 dark:hover:text-primary-400"
            >
              {t('auth.login')}
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50 dark:bg-gray-950">
      {/* Theme toggle in top right */}
      <div className="absolute top-4 right-4">
        <button
          onClick={toggleTheme}
          className="rounded-md p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-50"
          aria-label="Toggle theme"
        >
          {resolvedTheme === 'dark' ? (
            <RiSunLine className="size-5" />
          ) : (
            <RiMoonLine className="size-5" />
          )}
        </button>
      </div>

      <div className="flex flex-1 items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
        <div className="w-full max-w-sm space-y-8">
          {/* Logo and title */}
          <div className="text-center">
            <Logo size="lg" />
            <h1 className="mt-8 font-display text-display text-gray-950 dark:text-gray-50">
              {t('auth.registerTitle')}
            </h1>
          </div>

          {/* Register form */}
          <form className="space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="rounded-md bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4">
                <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
              </div>
            )}

            <div className="space-y-4">
              <Input
                label={t('auth.name')}
                type="text"
                name="name"
                autoComplete="name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
              />

              <Input
                label={t('auth.email')}
                type="email"
                name="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="email@example.com"
              />

              <Input
                label={t('auth.password')}
                type="password"
                name="password"
                autoComplete="new-password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                enableShowPassword
              />
            </div>

            <Button
              type="submit"
              loading={isLoading}
              className="w-full"
            >
              {t('auth.register')}
            </Button>

            <p className="text-center text-sm text-gray-500 dark:text-gray-400">
              {t('auth.hasAccount')}{' '}
              <Link
                to="/login"
                className="font-medium text-primary-500 hover:text-primary-600 dark:hover:text-primary-400"
              >
                {t('auth.login')}
              </Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  )
}
